import type { CatalogV2Product, CatalogV2Variant } from '../types/catalogV2Product';
import { toCatalogV2VariationNameValues } from '../types/catalogV2Product';
import type { SectionBasedProduct, ConciergeProductV2Detail } from './types';
import { resolveBrandclubHostDomain } from '../utils/domain';

const getDefaultApiBaseUrl = () => resolveBrandclubHostDomain();

// Shared `productV2` selection for the full detail shape the panel consumes.
const PRODUCT_V2_DETAIL_FIELDS = `
  stacklineSku
  parentStacklineSku
  catalogSku
  title
  imageUrl
  imageUrls
  brandId
  brandName
  retailPrice
  compareAtPrice
  retailerId
  retailerSku
  retailerName
  retailerProductPageUrl
  isAvailable
  checkoutEligible
  whereToBuyFallback
  whereToBuyOfferRetailerIds
  featureBullets
  description
  rewardAmount
  reviewSummary {
    averageRating
    reviewCount
  }
  variantOptions {
    name
    value
  }
  variants {
    stacklineSku
    title
    retailPrice
    compareAtPrice
    imageUrl
    retailerProductPageUrl
    featureBullets
    isAvailable
    availabilityStatus
    checkoutEligible
    whereToBuyFallback
    whereToBuyOfferRetailerIds
    options {
      name
      value
    }
    offers {
      stacklineSku
      retailerId
      retailerSku
      retailerName
      retailPrice
      compareAtPrice
      retailerProductPageUrl
      imageUrls
      isPrimary
      rewardAmount
    }
  }
  offers {
    stacklineSku
    retailerId
    retailerSku
    retailerName
    retailPrice
    compareAtPrice
    retailerProductPageUrl
    imageUrls
    isPrimary
    rewardAmount
  }
`;

/**
 * Concierge product data access — all product-related API calls for the concierge
 * surface, encapsulated so the request/shape/caching machinery lives in one place.
 *
 * A single shared instance ({@link productApi}) backs every caller: the product detail
 * cache is keyed by `apiBaseUrl + sku`, so duplicate panel opens share one request.
 * Construct additional instances only when you deliberately want isolated caches.
 */
export class ProductApi {
  // ── Product-with-variants cache ────────────────────────────────────────────
  // Lets the product carousel preload detail while the user reads the list so the
  // side panel opens instantly. Keyed by apiBaseUrl + sku; successful results are
  // memoized, in-flight requests deduped, and failures (null) evicted so a later
  // open can retry.
  private readonly productCache = new Map<string, ConciergeProductV2Detail>();
  private readonly productInflight = new Map<string, Promise<ConciergeProductV2Detail | null>>();

  /**
   * Fetch product metadata by stacklineSku via the concierge product endpoint
   * (V1 REST) — used for the pinned bar.
   */
  async fetchByStacklineSku({
    stacklineSku,
    headersToInject,
    apiBaseUrl = getDefaultApiBaseUrl(),
  }: {
    stacklineSku: string;
    headersToInject?: Record<string, string>;
    apiBaseUrl?: string;
  }): Promise<SectionBasedProduct | null> {
    try {
      const headers: Record<string, string> = {
        ...headersToInject,
        'Content-Type': 'application/json',
      };

      const response = await fetch(`${apiBaseUrl}/api-rewards/product/${stacklineSku}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch product: ${response.status}`);
      }

      const data = await response.json();
      return data.product || data;
    } catch (error) {
      console.error('Error fetching product by stacklineSku:', error);
      return null;
    }
  }

  /**
   * Fetch product metadata by stacklineSku from the Product V2 catalog via the
   * `productV2` consumer GraphQL resolver. Used by branded (Product V2 catalog)
   * apps, where the SKU lives only in the V2 channel/catalog indices and the V1
   * REST endpoint (`/api-rewards/product/:sku`) would 404. `appId` is inferred
   * server-side from the `x-bc-app-id` header injected at the edge.
   */
  async fetchV2ByStacklineSku({
    stacklineSku,
    headersToInject,
    apiBaseUrl = getDefaultApiBaseUrl(),
  }: {
    stacklineSku: string;
    headersToInject?: Record<string, string>;
    apiBaseUrl?: string;
  }): Promise<SectionBasedProduct | null> {
    try {
      const headers: Record<string, string> = {
        ...headersToInject,
        'Content-Type': 'application/json',
      };

      const query = `
        query ConciergePinnedProductV2($stacklineSku: String!) {
          productV2(stacklineSku: $stacklineSku) {
            stacklineSku
            title
            imageUrl
            imageUrls
            reviewSummary {
              averageRating
              reviewCount
            }
          }
        }
      `;

      const response = await fetch(
        `${apiBaseUrl.replace(/\/+$/, '')}/api-rewards/graphql?id=ConciergePinnedProductV2`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ query, variables: { stacklineSku } }),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch product V2: ${response.status}`);
      }

      const result = await response.json();
      const product = result?.data?.productV2 as CatalogV2Product | undefined;
      if (!product) {
        return null;
      }

      return {
        ...product,
        imageUrls: product.imageUrls ?? (product.imageUrl ? [product.imageUrl] : []),
        reviewsRating: product.reviewSummary?.averageRating,
        reviewsAverageRating: product.reviewSummary?.averageRating,
        reviewsCount: product.reviewSummary?.reviewCount,
      } as SectionBasedProduct;
    } catch (error) {
      console.error('Error fetching product V2 by stacklineSku:', error);
      return null;
    }
  }

  /**
   * Fetch a product plus its variant family and cross-retailer offers by
   * stacklineSku, from the **V2 catalog** via the `productV2` GraphQL query
   * (the concierge is a V2 surface — the legacy V1 `/with-variants` REST call read
   * the wrong store). Shapes the result into the `ConciergeProductV2Detail` the product
   * detail panel consumes.
   */
  async fetchWithVariants({
    stacklineSku,
    headersToInject,
    apiBaseUrl = getDefaultApiBaseUrl(),
  }: {
    stacklineSku: string;
    headersToInject?: Record<string, string>;
    apiBaseUrl?: string;
  }): Promise<ConciergeProductV2Detail | null> {
    try {
      const headers: Record<string, string> = {
        ...headersToInject,
        'Content-Type': 'application/json',
      };

      const query = `
        query ConciergeProductDetailV2($stacklineSku: String!) {
          productV2(stacklineSku: $stacklineSku) {
            ${PRODUCT_V2_DETAIL_FIELDS}
          }
        }
      `;

      const response = await fetch(
        `${apiBaseUrl.replace(/\/+$/, '')}/api-rewards/graphql?id=ConciergeProductDetailV2`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ query, variables: { stacklineSku } }),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch product with variants: ${response.status}`);
      }

      const result = await response.json();
      const product = result?.data?.productV2;
      if (!product) {
        return null;
      }

      return this.mapDetail(product);
    } catch (error) {
      console.error('Error fetching product with variants:', error);
      return null;
    }
  }

  /** Synchronous read of an already-resolved product, or undefined if not cached. */
  getCachedWithVariants(stacklineSku: string, apiBaseUrl?: string): ConciergeProductV2Detail | undefined {
    return this.productCache.get(this.productCacheKey(stacklineSku, apiBaseUrl));
  }

  /**
   * Cached + deduped variant of {@link fetchWithVariants}. Multiple callers
   * (e.g. carousel preload + panel open) for the same SKU share one request.
   */
  fetchWithVariantsCached(params: {
    stacklineSku: string;
    headersToInject?: Record<string, string>;
    apiBaseUrl?: string;
  }): Promise<ConciergeProductV2Detail | null> {
    const key = this.productCacheKey(params.stacklineSku, params.apiBaseUrl);
    const cached = this.productCache.get(key);
    if (cached) return Promise.resolve(cached);
    const inflight = this.productInflight.get(key);
    if (inflight) return inflight;

    const promise = this.fetchWithVariants(params).then((product) => {
      this.productInflight.delete(key);
      if (product) this.productCache.set(key, product);
      return product;
    });
    this.productInflight.set(key, promise);
    return promise;
  }

  /** Fire-and-forget preload; swallows errors since it's purely opportunistic. */
  prefetchWithVariants(params: {
    stacklineSku: string;
    headersToInject?: Record<string, string>;
    apiBaseUrl?: string;
  }): void {
    void this.fetchWithVariantsCached(params).catch(() => {});
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  /** Shape a raw `productV2` detail payload into the `ConciergeProductV2Detail` the panel consumes. */
  private mapDetail(product: CatalogV2Product): ConciergeProductV2Detail {
    const variants: CatalogV2Variant[] = (product.variants ?? []).map((variant) => ({
      ...variant,
      variationNameValues: toCatalogV2VariationNameValues(variant.options),
    }));

    // The queried product's own option map (top-level `options`) drives the
    // "selected" state in the variant selector. We match on option *values*, not
    // stacklineSku, because the queried sku (often a catalog sku) need not equal
    // any family member's channel sku.
    const ownVariationNameValues = toCatalogV2VariationNameValues(product.variantOptions);

    // A purchase-reward campaign can be attached to a secondary channel offer
    // (e.g. the Amazon offer) rather than the primary catalog product, so the
    // top-level `rewardAmount` is often 0 even when the product is rewardable.
    // Surface the max reward across the product and all its offers.
    const offerRewards = (product.offers ?? [])
      .map((offer) => offer.rewardAmount)
      .filter((amount: number | undefined): amount is number => typeof amount === 'number');
    const maxRewardAmount = Math.max(product.rewardAmount ?? 0, ...offerRewards, 0);

    // Build a synthetic active promotion from the Shopify compare-at price so the
    // panel's `getDisplayPrice` renders the strikethrough + savings unchanged.
    const retailPrice: number = product.retailPrice ?? 0;
    const compareAtPrice: number | undefined = product.compareAtPrice;
    const activeProductPromotion =
      typeof compareAtPrice === 'number' && compareAtPrice > retailPrice
        ? {
            isActive: true,
            promoPrice: retailPrice,
            listPrice: compareAtPrice,
            discountPercentage: (compareAtPrice - retailPrice) / compareAtPrice,
            discountAmount: compareAtPrice - retailPrice,
            promoType: '',
            updatedTime: '',
            '@promoUpdatedTime': '',
          }
        : undefined;

    return {
      ...product,
      imageUrls: product.imageUrls ?? (product.imageUrl ? [product.imageUrl] : []),
      reviewsRating: product.reviewSummary?.averageRating,
      reviewsAverageRating: product.reviewSummary?.averageRating,
      reviewsCount: product.reviewSummary?.reviewCount,
      totalRewardAmount: maxRewardAmount,
      hasRewards: maxRewardAmount > 0,
      variants,
      variationNameValues: ownVariationNameValues,
      activeProductPromotion,
    } as ConciergeProductV2Detail;
  }

  private productCacheKey(stacklineSku: string, apiBaseUrl?: string): string {
    return `${apiBaseUrl ?? ''}|${stacklineSku}`;
  }
}

/**
 * Shared concierge product API instance. Use this everywhere so product-with-variants
 * cache entries are shared across callers.
 */
export const productApi = new ProductApi();
